/**
 * ManaCurveSparkline -- inline SVG line sparkline for mana curve distribution.
 * Props: curve is an array of counts for mana values 0-7+.
 */

interface ManaCurveSparklineProps {
  curve: number[];
  width?: number;
  height?: number;
}

export function ManaCurveSparkline({ curve, width = 120, height = 40 }: ManaCurveSparklineProps) {
  const padX = 4;
  const padY = 4;
  const max = Math.max(...curve, 1);
  const n = curve.length;
  if (n < 2) return null;

  const usableW = width - padX * 2;
  const usableH = height - padY * 2;

  const points = curve.map((v, i) => {
    const x = padX + (i / (n - 1)) * usableW;
    const y = padY + usableH - (v / max) * usableH;
    return [x, y] as [number, number];
  });

  // Catmull-Rom → cubic bezier smooth path
  function smoothPath(pts: [number, number][], tension = 0.4): string {
    if (pts.length < 2) return '';
    const d: string[] = [`M${pts[0][0].toFixed(2)},${pts[0][1].toFixed(2)}`];
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[Math.max(i - 1, 0)];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[Math.min(i + 2, pts.length - 1)];
      const cp1x = p1[0] + (p2[0] - p0[0]) * tension;
      const cp1y = p1[1] + (p2[1] - p0[1]) * tension;
      const cp2x = p2[0] - (p3[0] - p1[0]) * tension;
      const cp2y = p2[1] - (p3[1] - p1[1]) * tension;
      d.push(`C${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${p2[0].toFixed(2)},${p2[1].toFixed(2)}`);
    }
    return d.join(' ');
  }

  const linePath = smoothPath(points);

  // Fill area under line (close down to bottom)
  const bottom = (padY + usableH).toFixed(2);
  const fillPath = [
    `M${points[0][0].toFixed(2)},${bottom}`,
    `L${points[0][0].toFixed(2)},${points[0][1].toFixed(2)}`,
    smoothPath(points).replace(/^M[\d.]+,[\d.]+ /, ''),
    `L${points[n - 1][0].toFixed(2)},${bottom}`,
    'Z',
  ].join(' ');

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block', margin: '4px auto' }}
    >
      {/* Area fill */}
      <path d={fillPath} fill="var(--primary, #7baaf7)" opacity={0.15} />
      {/* Line */}
      <path d={linePath} fill="none" stroke="var(--primary, #7baaf7)" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" opacity={0.85} />
      {/* Dots at each data point */}
      {points.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={1.5} fill="var(--primary, #7baaf7)" opacity={0.9} />
      ))}
    </svg>
  );
}
